import { useEffect, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import VisibilityIcon from "@mui/icons-material/Visibility";
import Pagination from "../../../components/Pagination";
import Table from "../../../components/table";
import usePagination from "../../../components/usePagination";
import api from "../../../api/api";
import * as bootstrap from "bootstrap";
import "../../Freight/Common/form.css";
import Breadcrumbs from "../../../components/Breadcrumbs";
import SearchIcon from "@mui/icons-material/Search";
 
const TOTAL_FOLLOWUPS_FILTER_KEY = "totalFollowupsFilters";
 
const TotalFollowups = () => {
  const location = useLocation();
 
  /* ================= RESTORE FILTERS ================= */
  const storedFilters =
    JSON.parse(localStorage.getItem(TOTAL_FOLLOWUPS_FILTER_KEY)) || {};
 
  const { fromDate, toDate, createdBy } = location.state || storedFilters;
 
  const [followups, setFollowups] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [search, setSearch] = useState("");
 
  /* ================= SAVE FILTERS ================= */
  useEffect(() => {
    const filters = {
      fromDate: fromDate || null,
      toDate: toDate || null,
      createdBy: createdBy || null,
    };
 
    localStorage.setItem(TOTAL_FOLLOWUPS_FILTER_KEY, JSON.stringify(filters));
  }, [fromDate, toDate, createdBy]);
 
  /* ================= HANDLE PAGE RELOAD ================= */
  useEffect(() => {
    const handleBeforeUnload = () => {
      sessionStorage.setItem("totalFollowupsReload", "true");
    };
 
    window.addEventListener("beforeunload", handleBeforeUnload);
    return () => window.removeEventListener("beforeunload", handleBeforeUnload);
  }, []);
 
  useEffect(() => {
    const isReload = sessionStorage.getItem("totalFollowupsReload");
 
    if (isReload) {
      sessionStorage.removeItem("totalFollowupsReload");
      localStorage.removeItem(TOTAL_FOLLOWUPS_FILTER_KEY);
    }
  }, []);
 
  /* ================= FETCH FOLLOWUPS ================= */
  useEffect(() => {
    const fetchFollowups = async () => {
      setLoading(true);
      try {
        const params = {};
        if (fromDate) params.fromDate = fromDate;
        if (toDate) params.toDate = toDate;
        if (createdBy) params.CreateBY = createdBy;
 
        const response = await api.get("/followups", { params });
 
        let followupsData = [];
 
        if (response.data.followups && Array.isArray(response.data.followups)) {
          followupsData = response.data.followups;
        } else if (
          response.data.clients &&
          Array.isArray(response.data.clients)
        ) {
          response.data.clients.forEach((client) => {
            if (
              client.follow_up_number > 0 &&
              Array.isArray(client.follow_ups)
            ) {
              client.follow_ups.forEach((fu) => {
                followupsData.push({
                  ...fu,
                  first_name: client.first_name,
                  last_name: client.last_name,
                  company_name: client.company_name,
                  customer_industry: client.customer_industry,
                  created_by: client.created_by,
                });
              });
            }
          });
        }
 
        setFollowups(followupsData);
      } catch (err) {
        console.error(err);
        setError("Failed to fetch followups");
      } finally {
        setLoading(false);
      }
    };
 
    fetchFollowups();
  }, [fromDate, toDate, createdBy]);
 
  /* ================= SEARCH FILTER ================= */
  const filteredFollowups = followups.filter((fu) => {
    const term = search.toLowerCase();
    return (
      fu.first_name?.toLowerCase().includes(term) ||
      fu.last_name?.toLowerCase().includes(term) ||
      fu.company_name?.toLowerCase().includes(term)
    );
  });
 
  /* ================= PAGINATION ================= */
  const {
    currentPage,
    itemsPerPage,
    totalPages,
    totalItems,
    currentItems,
    goToPage,
    handleItemsPerPageChange,
  } = usePagination(filteredFollowups, 10);
 
  /* ================= TOOLTIP ================= */
  useEffect(() => {
    const tooltipTriggerList = Array.from(
      document.querySelectorAll('[data-bs-toggle="tooltip"]'),
    );
    tooltipTriggerList.forEach((el) => new bootstrap.Tooltip(el));
  }, [filteredFollowups, currentPage]);
 
  /* ================= TABLE COLUMNS ================= */
  const columns = [
    {
      label: "Client Name",
      render: (fu) => (
        <span
          data-bs-toggle="tooltip"
          title={`${fu.first_name || ""} ${fu.last_name || ""}`}
        >
          {fu.first_name} {fu.last_name}
        </span>
      ),
    },
    {
      label: "Company Name",
      render: (fu) => (
        <span data-bs-toggle="tooltip" title={fu.company_name || "-"}>
          {fu.company_name || "-"}
        </span>
      ),
    },
    {
      label: "Industry Type",
      render: (fu) => (
        <span data-bs-toggle="tooltip" title={fu.customer_industry || "-"}>
          {fu.customer_industry || "-"}
        </span>
      ),
    },
    {
      label: "Handled By",
      render: (fu) => fu.created_by || "-",
    },
    {
      label: "Date",
      render: (fu) => {
        if (!fu.created_at) return "-";
        const dateStr = fu.created_at.split("T")[0];
        const timePart = fu.created_at.split("T")[1]?.split(".")[0] || "";
        const [hours, minutes] = timePart.split(":");
        const hour = parseInt(hours, 10);
        const ampm = hour >= 12 ? "PM" : "AM";
        const hour12 = hour % 12 || 12;
        return `${dateStr} ${hour12}:${minutes} ${ampm}`;
      },
    },
    {
      label: "Action",
      render: (fu) => (
        <Link to={`/view-followup-details/${fu.customer_id}?type=followup`}>
          <VisibilityIcon
            style={{ fontSize: 20, cursor: "pointer", color: "#1976d2" }}
            title="View Details"
          />
        </Link>
      ),
    },
  ];
 
  if (error) return <p className="text-danger">{error}</p>;
 
  /* ================= RENDER ================= */
  return (
    <>
      <Breadcrumbs />
 
      <div className="mb-3 position-relative" style={{ maxWidth: "100%" }}>
        <input
          type="search"
          className="form-control ps-5"
          placeholder="Search by Client Name or Company Name..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
        />
 
        <SearchIcon
          style={{
            position: "absolute",
            left: "12px",
            top: "50%",
            transform: "translateY(-50%)",
            cursor: "pointer",
            color: "#6b7280",
            width: "16px",
            height: "16px",
          }}
        />
      </div>
 
      <Table
        columns={columns}
        data={currentItems}
        loading={loading}
        emptyMessage="No Followups found."
        itemsPerPage={itemsPerPage}
        pagination={
          <Pagination
            currentPage={currentPage}
            totalPages={totalPages}
            onPageChange={goToPage}
            itemsPerPage={itemsPerPage}
            onItemsPerPageChange={handleItemsPerPageChange}
            totalItems={totalItems}
          />
        }
      />
    </>
  );
};
 
export default TotalFollowups;
 